import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {AccessibleGridCell, Grid, isFullyAccessibleGrid, TransferFlow} from 'constants/SampleConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import GridCellToolbarAnnotateFixedPlateButton from 'samples/grids/GridCellToolbarAnnotateFixedPlateButton';
import GridCellToolbarFillFixedPlateButton from 'samples/grids/GridCellToolbarFillFixedPlateButton';
import useGridContentAccessibility from 'samples/grids/hooks/useGridContentAccessibility';
import {PLATE_MAP_MAX_GRID_HEIGHT, PLATE_MAP_MAX_GRID_WIDTH} from 'samples/plate-maps/PlateMapConstants';
import {hasSolidUnits} from 'samples/plate-maps/PlateMapUtil';
import {
  hasNewPlateAnnotationFillFeatures,
  isFixedPlate,
  isGridSchemaTooBigForNewPlateFeatures,
} from 'samples/SampleUtil';
import TransferModal from 'samples/transfers/TransferModal';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';

export interface GridCellToolbarActionButtonsProps {
  grid: Grid;
  selectedCells: Array<AccessibleGridCell>;
}

/**
 * Get the fill and/or annotate buttons that are enabled/applicable to the current grid.
 */
export default function GridCellToolbarActionButtons({
  grid,
  selectedCells,
}: GridCellToolbarActionButtonsProps): JSX.Element {
  const {status, inaccessibleContainedItemIds} = useGridContentAccessibility(grid);
  const isPending = status === QueryResultType.PENDING;
  // If the query hasn't completed, or it has but it errored (so we don't know whether anything is
  // inaccessible), or we know we have inaccessible contents, block the new options.
  const contentAccessibilityBlocksNewGridFillOptions =
    isPending || status === QueryResultType.ERROR || inaccessibleContainedItemIds.length > 0;

  const hasAnySelected = Boolean(selectedCells.length);
  // If not all containers in the plate are accessible, don't show the new grid annotation tools. This constraint
  // has no impact for fixed plates (all wells will be accessible), but friendly handling for inaccessible
  // containers will be needed in order to support the new features for matrix plates.
  const showNewGridAnnotationOptions = hasNewPlateAnnotationFillFeatures(grid) && isFullyAccessibleGrid(grid);

  // Don't use the new fill plate flow for grids with solid units or inaccessible contents.
  const gridHasSolidUnits = hasSolidUnits(grid);
  const showNewGridFillOptions =
    showNewGridAnnotationOptions && !gridHasSolidUnits && !contentAccessibilityBlocksNewGridFillOptions;

  const oldFillFlowMessage: string | null = (() => {
    if (!BenchlingConfig.INVENTORY_ENABLE_BULK_ANNOTATE_AND_FILL_ON_PLATES) {
      return null;
    }
    if (!isFixedPlate(grid)) {
      return 'The updated fill wells tool is only available for fixed plates';
    } else if (contentAccessibilityBlocksNewGridFillOptions) {
      return 'Access to all contents is needed to use the updated fill wells tool';
    } else if (gridHasSolidUnits) {
      return 'The updated fill wells tool does not support plates with solid contents';
    } else if (isGridSchemaTooBigForNewPlateFeatures(grid.storableSchema)) {
      return `The updated fill wells tool does not support plates larger than ${PLATE_MAP_MAX_GRID_HEIGHT} wells by ${PLATE_MAP_MAX_GRID_WIDTH} wells`;
    } else {
      return null;
    }
  })();

  const gridType = APIUtil.apiIdToType(grid.id);
  const transfersTitle = gridType === ModelTypeConstants.BOX ? 'Fill containers' : 'Fill wells';

  return (
    <div className="buttonGroup">
      {showNewGridAnnotationOptions && (
        <GridCellToolbarAnnotateFixedPlateButton
          key="annotate-fixed-plate-button"
          grid={grid}
          selectedCells={selectedCells}
        />
      )}
      {showNewGridFillOptions ? (
        <GridCellToolbarFillFixedPlateButton
          key="fill-fixed-plate-button"
          grid={grid}
          selectedCells={selectedCells}
        />
      ) : (
        <Button
          key="fill-containers-button"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={!hasAnySelected || isPending}
          onClick={() => {
            runModal(TransferModal, {
              flow: TransferFlow.TRANSFER_IN,
              registryId: grid.storableSchema.registryId,
              title: transfersTitle,
              preselectedOutputConfig: {
                outputType: ModelTypeConstants.PLATE,
                outputPlate: grid,
                outputCells: selectedCells,
              },
              actionOrigin:
                gridType === ModelTypeConstants.BOX
                  ? ActionOrigin.INVENTORY_BOX_PAGE_GRID_CELL_TOOLBAR
                  : ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
              inputPaneInfoBannerMessage: oldFillFlowMessage,
            });
          }}
          size={ButtonSize.SMALL}
          text={transfersTitle}
          dataTestAttrs={{
            component: 'Button',
            key: transfersTitle === 'Fill containers' ? 'fillContainers' : 'fillWells',
          }}
        />
      )}
    </div>
  );
}
